import { createContext, type ReactElement, type ReactNode, useContext, useMemo } from "react"
import { useEntitlements } from "~/features/entitlements/api/useEntitlements"
import { useEbookIndexSource } from "./api/useEbookIndexSource"
import { buildStudyEbookIndex, EMPTY_STUDY_EBOOK_INDEX } from "./studyEbookIndex"
import type { StudyEbookIndex, StudyEbookLookup, StudyMaterial } from "./types"

/**
 * Carries "which ebook accompanies this study" to the two places that ask: the
 * study detail screen and the Studies cards on the library page.
 *
 * A provider rather than a hook per consumer because both halves of the answer
 * are lists — every ebook and every seat — and rebuilding the index once per
 * library card would do that work for each card in the grid.
 *
 * Outside the provider the index is empty and both surfaces render exactly as
 * they did before, which is also what happens with the POC facade off: the seat
 * half goes quiet and the graph half carries on alone.
 */
const StudyEbookContext = createContext<StudyEbookIndex>(EMPTY_STUDY_EBOOK_INDEX)

export const StudyEbookProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { assets } = useEbookIndexSource()
  const { items } = useEntitlements()

  const index = useMemo(() => buildStudyEbookIndex(assets, items), [assets, items])

  if (import.meta.env.DEV && index.ambiguousKeys.length > 0) {
    console.info(
      `[study-ebook] ${index.ambiguousKeys.length} title(s) matched more than one ebook, so those studies show no material: ${index.ambiguousKeys.join(", ")}`,
    )
  }

  return <StudyEbookContext.Provider value={index}>{children}</StudyEbookContext.Provider>
}

/** The ebook accompanying a study, or undefined when nothing matches it unambiguously. */
export const useStudyEbook = (studyTitle: string | undefined): StudyMaterial | undefined => {
  const index = useContext(StudyEbookContext)

  return studyTitle ? index.lookup(studyTitle) : undefined
}

/** The same question with the reason kept. Only the provenance overlay wants this. */
export const useStudyEbookLookup = (studyTitle: string | undefined): StudyEbookLookup => {
  const index = useContext(StudyEbookContext)

  return studyTitle ? index.resolve(studyTitle) : { status: "inactive" }
}
